<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Makercart orders</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">Order’s List</div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <div class="mb-3" style="display: flex; gap: 10px; flex-wrap: wrap"></div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="true"
        :is-view-action="true"
        :is-edit-action="false"
        :is-delete-action="false"
        @action="handleAction"
        @sort="handleTableSort"
        :use-api-sort="true"
        :customActions="[]"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      />
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import makerCartAdminService from "@/services/makerCartAdminService";
import { debounce } from "lodash";
import TableData from "@/components/common/TableData.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";
import DateFormatter from "@/utils/DateFormatter.js";

const tableLoading = ref(true);
const searchQuery = ref("");
const currentStatus = ref("");

const data = ref({
  message: "",
  error: null,
  data: {
    page: 1,
    limit: 10,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  sort_by: "datetime",
  order: "desc",
});

useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

onMounted(async () => {
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

// Define table headers
const headers = ref([
  { text: "Order Code", value: "order_code", sortable: true },
  { text: "Date", value: "created_at", sortable: true },
  { text: "User Email", value: "user_email", sortable: false },
  { text: "Activity(s)", value: "activities_count", sortable: false },
]);

// Map API data to table items
const tableItems = computed(() => {
  return (data.value?.data?.rows || []).map((item) => ({
    id: item.id,
    order_code: item.order_code || "-",
    created_at: DateFormatter.formatToUserTimezone(item.created_at) || "-",
    user_email: item.user?.email || "-",
    activities_count: (item.stem_makercart_order_items || []).length,
  }));
});

const getAll = async () => {
  try {
    tableLoading.value = true;

    const response = await makerCartAdminService.getAll(filters.value);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.log(error.message);
  } finally {
    tableLoading.value = false;
  }
};

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Debounced search handler
const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

// Handle sorting from TableData
const handleTableSort = ({ sort_by, sort_dir }) => {
  // Map UI column to API sort fields
  const apiSortBy = sort_by === "created_at" ? "datetime" : sort_by;
  filters.value.sort_by = apiSortBy;
  filters.value.order = sort_dir.toLowerCase();
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
};

// Handle actions (view details)
const router = useRouter();
const handleAction = (action) => {
  if (action.type === "view") {
    router.push({ name: "MakerCartFormPage", params: { id: action.item.id } });
  }
};
</script>

<style>
/* General button styling */
.button-coach {
  display: inline-block;
  padding: 5px 20px;
  margin: 10px 0;
  border: 2px solid transparent;
  border-radius: 4px;
  font-size: var(--fs-medium);
  text-align: center;
  cursor: pointer;
  text-decoration: none;
}

/* Accepted button styling */
.button-coach-accepted {
  color: #002d72;
  border-color: #002d72;
}

/* Pending button styling */
.button-coach-pending {
  color: #000000;
  border-color: #000000;
}

/* Rejected button styling */
.button-coach-rejected {
  color: #d0021b;
  border-color: #d0021b;
}
</style>
